import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";

import Modal from "../../../../components/modal";
import api from "../../../../services/api";
import Loader from "../../../../components/loader";
import { classNames } from "../../../../utils";

import General from "./general";
import Amount from "./amount";
import Signature from "./signature";
import Invoices from "./invoices";
import Expenses from "./expenses";
import CommentsTab from "./comments";

export default ({ budget, onClose, onDone }) => {
  const [values, setValues] = useState(budget);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("general");
  const currentUser = useSelector((state) => state.Auth.user);

  useEffect(() => {
    setValues(budget);
    setTab("general");
  }, [budget]);

  if (!values) return null;

  const handleSubmit = async () => {
    if (!values.name) return toast.error("Missing name !");
    setLoading(true);
    try {
      const { ok, code } = await api.put(`/budget/${values._id}`, values);
      if (!ok) return toast.error(code || "Failed to update");
      toast.success("Updated!");
      onDone?.();
      onClose();
    } catch (error) {
      console.error(error);
      toast.error("Failed to update");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    const confirm = window.confirm("Are you sure you want to delete this budget ?");
    if (!confirm) return;

    try {
      const { ok, code } = await api.remove(`/budget/${values._id}`);
      if (!ok) return toast.error(code);
      toast.success("Deleted!");
      onDone?.();
      onClose();
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete");
    }
  };

  async function onUpdate(obj) {
    const newValue = { ...values, ...obj };

    // Handle frequency calculation
    if (newValue.frequency === "monthly") {
      const duration = newValue.duration || 1;
      newValue.amount = newValue.value * duration;
    } else {
      newValue.amount = newValue.value;
    }

    // If duration or startAt changed, update endAt
    if (obj.duration || obj.startAt) {
      if (newValue.startAt && newValue.duration) {
        const startDate = new Date(newValue.startAt);
        const endDate = new Date(startDate);
        endDate.setMonth(startDate.getMonth() + parseInt(newValue.duration));
        newValue.endAt = endDate.toISOString().split("T")[0];
      }
    }

    // If endAt changed, recalculate duration based on startAt
    if (obj.endAt && newValue.startAt) {
      const startDate = new Date(newValue.startAt);
      const endDate = new Date(newValue.endAt);
      const monthDiff = (endDate.getFullYear() - startDate.getFullYear()) * 12 + (endDate.getMonth() - startDate.getMonth());
      newValue.duration = monthDiff > 0 ? monthDiff : 1;
    }

    setValues(newValue);
  }

  return (
    <Modal isOpen onClose={onClose} className="w-3/4">
      <div className="px-8 py-6">
        <nav className="flex flex-row w-full border-b border-gray-200 mb-6">
          <TabItem title="General" tab="general" setTab={setTab} active={tab === "general"} />
          <TabItem title="Amount" tab="amount" setTab={setTab} active={tab === "amount"} />
          <TabItem title="Signature & Legal" tab="signature" setTab={setTab} active={tab === "signature"} />
          <TabItem title="Invoices" tab="invoices" setTab={setTab} active={tab === "invoices"} />
          <TabItem title="Expenses" tab="expenses" setTab={setTab} active={tab === "expenses"} />
          <TabItem title="Comments" tab="comments" setTab={setTab} active={tab === "comments"} />
        </nav>

        <div className="h-[60vh] overflow-y-auto pr-1">
          {tab === "general" && <General values={values} setValues={setValues} />}
          {tab === "amount" && <Amount values={values} setValues={setValues} onUpdate={onUpdate} />}
          {tab === "signature" && <Signature values={values} setValues={setValues} currentUser={currentUser} projectId={values.projectId} />}
          {tab === "invoices" && <Invoices values={values} projectId={values.projectId} />}
          {tab === "expenses" && <Expenses values={values} projectId={values.projectId} />}
          {tab === "comments" && <CommentsTab values={values} setValues={setValues} currentUser={currentUser} />}
        </div>

        <div className="flex items-center gap-2 text-sm text-gray-600 mt-6">
          Created By {values?.userName || "Unknown User"} the {values?.createdAt?.split("T")[0]}
        </div>
        <div className="flex items-center justify-between pt-6 border-t">
          <button className="flex items-center px-4 py-2 text-red-600 hover:text-red-800 transition-colors" onClick={handleDelete} disabled={loading}>
            Delete Budget
          </button>
          <div className="flex items-center gap-3">
            <button className="gray-btn" onClick={onClose}>
              Cancel
            </button>
            <button className="blue-btn" onClick={handleSubmit} disabled={loading}>
              {loading ? <Loader size="small" color="white" /> : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

const TabItem = ({ tab, title, setTab, active }) => (
  <button
    onClick={() => setTab(tab)}
    className={classNames(
      "group inline-flex items-center px-4 py-2 cursor-pointer text-sm text-gray-500 hover:border-gray-300 hover:text-gray-700 border-b-2 ",
      active ? "text-primary  border-primary" : "border-transparent",
    )}>
    <div className="flex items-center gap-2">{title}</div>
  </button>
);
